'use client';

import type { TranslationKey } from '@freedraw/i18n';
import { useAppTranslation } from '@freedraw/i18n/react';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useEffect, useId, useRef, useState, type ComponentType } from 'react';

import { DISPLAY_FONT } from '@/components/landing/landing-fonts';
import {
  AiDiagramIllustration,
  ShareWorkspaceIllustration,
  SketchCanvasIllustration
} from '@/components/landing/landing-illustrations';
import { MermaidDemo } from '@/components/landing/landing-mermaid';
import { playLandingVisual } from '@/components/landing/landing-motion';
import { BRAND } from '@/theme';

const DARK = '#171C28';
const MUTED = 'rgba(255, 255, 255, 0.62)';

type Tab = {
  id: string;
  titleKey: TranslationKey;
  bodyKey: TranslationKey;
  Visual: ComponentType<{ label: string }>;
};

const TABS: Tab[] = [
  { id: 'ai', titleKey: 'landing.tabAiTitle', bodyKey: 'landing.tabAiBody', Visual: AiDiagramIllustration },
  { id: 'text', titleKey: 'landing.tabTextTitle', bodyKey: 'landing.tabTextBody', Visual: MermaidDemo },
  { id: 'sketch', titleKey: 'landing.tabSketchTitle', bodyKey: 'landing.tabSketchBody', Visual: SketchCanvasIllustration },
  { id: 'share', titleKey: 'landing.tabShareTitle', bodyKey: 'landing.tabShareBody', Visual: ShareWorkspaceIllustration }
];

export function LandingTabsSection() {
  const { t } = useAppTranslation();
  const baseId = useId();
  const [active, setActive] = useState(0);
  const visualRef = useRef<HTMLDivElement>(null);
  const switched = useRef(false);
  const tab = TABS[active]!;

  // The first visual animates in on scroll with the rest of the page; later ones play their
  // intro as they are switched in.
  useEffect(() => {
    if (!switched.current || !visualRef.current) {
      return undefined;
    }
    return playLandingVisual(visualRef.current);
  }, [active]);

  const select = (index: number) => {
    switched.current = true;
    setActive(index);
  };

  return (
    <Box
      component="section"
      id="how-it-works"
      sx={{
        scrollMarginTop: 64,
        position: 'relative',
        overflow: 'hidden',
        py: { xs: 9, md: 13 },
        bgcolor: DARK,
        color: '#ffffff',
        backgroundImage: `radial-gradient(60% 50% at 85% 0%, rgba(247, 181, 205, 0.16) 0%, transparent 70%), radial-gradient(rgba(255, 255, 255, 0.07) 1px, transparent 1px)`,
        backgroundSize: 'auto, 24px 24px'
      }}
    >
      <Container maxWidth="lg">
        <Box data-reveal sx={{ maxWidth: 720 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: BRAND.pink, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
            {t('landing.tabsEyebrow')}
          </Typography>
          <Typography
            component="h2"
            sx={{ mt: 1.5, fontFamily: DISPLAY_FONT, textWrap: 'balance', fontWeight: 600, fontSize: { xs: 36, md: 56 }, lineHeight: 1.04, letterSpacing: '-0.02em' }}
          >
            {t('landing.tabsTitle')}
          </Typography>
          <Typography sx={{ mt: 2, fontSize: { xs: 16, md: 18 }, lineHeight: 1.6, color: MUTED }}>
            {t('landing.tabsBody')}
          </Typography>
        </Box>

        <Stack
          data-reveal
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 4, md: 7 }}
          alignItems={{ md: 'center' }}
          sx={{ mt: { xs: 5, md: 8 } }}
        >
          <Stack role="tablist" aria-orientation="vertical" sx={{ flex: '0 0 auto', width: { md: 380 } }}>
            {TABS.map(({ id, titleKey, bodyKey }, index) => {
              const selected = index === active;
              return (
                <ButtonBase
                  key={id}
                  role="tab"
                  id={`${baseId}-tab-${id}`}
                  aria-selected={selected}
                  aria-controls={`${baseId}-panel`}
                  data-testid={`landing-tab-${id}`}
                  onClick={() => select(index)}
                  sx={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    py: 2.25,
                    pl: 2.5,
                    pr: 2,
                    fontFamily: 'inherit',
                    borderLeft: `2px solid ${selected ? BRAND.pink : 'rgba(255, 255, 255, 0.14)'}`,
                    bgcolor: selected ? 'rgba(255, 255, 255, 0.05)' : 'transparent',
                    borderRadius: '0 12px 12px 0',
                    transition: 'background-color 160ms ease, border-color 160ms ease',
                    '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.07)' },
                    '&.Mui-focusVisible': { outline: `2px solid ${BRAND.pink}`, outlineOffset: 2 }
                  }}
                >
                  <Stack direction="row" alignItems="baseline" spacing={1.5}>
                    <Typography component="span" sx={{ fontSize: 13, fontWeight: 700, color: selected ? BRAND.pink : MUTED, fontVariantNumeric: 'tabular-nums' }}>
                      {String(index + 1).padStart(2, '0')}
                    </Typography>
                    <Typography component="span" sx={{ fontSize: { xs: 20, md: 22 }, fontWeight: 700, color: selected ? '#ffffff' : MUTED }}>
                      {t(titleKey)}
                    </Typography>
                  </Stack>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateRows: selected ? '1fr' : '0fr',
                      transition: 'grid-template-rows 220ms ease',
                      '@media (prefers-reduced-motion: reduce)': { transition: 'none' }
                    }}
                  >
                    <Typography component="span" sx={{ display: 'block', overflow: 'hidden', pl: 4.25, pt: selected ? 1 : 0, fontSize: 15.5, lineHeight: 1.55, color: MUTED }}>
                      {t(bodyKey)}
                    </Typography>
                  </Box>
                </ButtonBase>
              );
            })}
          </Stack>

          <Box
            ref={visualRef}
            role="tabpanel"
            id={`${baseId}-panel`}
            aria-labelledby={`${baseId}-tab-${tab.id}`}
            data-testid="landing-tab-panel"
            sx={{
              flex: 1,
              minWidth: 0,
              p: { xs: 2, md: 3 },
              borderRadius: '22px',
              bgcolor: '#ffffff',
              color: BRAND.ink,
              boxShadow: '0 30px 80px rgba(0, 0, 0, 0.35)',
              minHeight: { md: 500 },
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <Box key={tab.id} sx={{ width: '100%', aspectRatio: tab.id === 'text' ? undefined : '3 / 2' }}>
              <tab.Visual label={t(tab.titleKey)} />
            </Box>
          </Box>
        </Stack>
      </Container>
    </Box>
  );
}
